import React, { useState, useCallback } from 'react'
import { Button, Spinner } from 'reactstrap'

import { usePubSub } from '@services'
import { testReconnection } from '@utils'
import { customToast, isDev } from '@common'

const WebsocketReconnect = () => {
    const { groups } = usePubSub()
    const [isTestingReconnection, setIsTestingReconnection] = useState(false)
    const [testStatus, setTestStatus] = useState(null)

    const joinedGroupName = Array.isArray(groups) && groups.length > 0 ? groups[0] : null

    const handleTestReconnection = useCallback(async () => {
        if (isTestingReconnection) return

        setIsTestingReconnection(true)
        setTestStatus(null)

        try {
            customToast.info('Starting reconnection test...', { duration: 2000 })

            const result = await testReconnection({
                onStatusChange: (event, status, groups, error) => {
                    if (event === 'status_change') {
                        setTestStatus({
                            status,
                            groups: groups || [],
                            error: error?.message,
                        })
                    }
                },
                onComplete: result => {
                    if (result.success) {
                        customToast.success('Reconnection test passed! Group automatically rejoined.', {
                            duration: 4000,
                        })
                    } else {
                        customToast.error(result.error || 'Reconnection test failed. Group was not automatically rejoined.', {
                            duration: 4000,
                        })
                    }
                },
                timeout: 15000,
            })

            setTestStatus({
                status: result.finalStatus?.connectionStatus || 'unknown',
                groups: result.finalStatus?.groups || [],
                success: result.success,
                error: result.error,
            })
        } catch (error) {
            customToast.error(`Reconnection test failed: ${error.message}`, { duration: 4000 })
            setTestStatus({
                status: 'error',
                groups: [],
                success: false,
                error: error.message,
            })
        } finally {
            setIsTestingReconnection(false)
        }
    }, [isTestingReconnection])

    if (!isDev) {
        return null
    }

    return (
        <div className="mb-3">
            <Button
                color="info"
                onClick={handleTestReconnection}
                disabled={isTestingReconnection || !joinedGroupName}
                className="w-100"
                style={{
                    fontSize: '1rem',
                    padding: '0.75rem',
                    fontWeight: '500',
                }}
            >
                {isTestingReconnection ?
                    <>
                        <Spinner size="sm" className="me-2" />
                        Testing Reconnection...
                    </>
                :   <>
                        <i className="bx bx-refresh me-2"></i>
                        Test Reconnection
                    </>
                }
            </Button>
            {testStatus && (
                <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#f8f9fa', fontSize: '0.875rem' }}>
                    <div className="fw-semibold mb-1">Test Status:</div>
                    <div>Status: {testStatus.status}</div>
                    {testStatus.groups.length > 0 && <div>Groups: {testStatus.groups.join(', ')}</div>}
                    {testStatus.error && <div className="text-danger">Error: {testStatus.error}</div>}
                    {testStatus.success !== undefined && (
                        <div className={testStatus.success ? 'text-success' : 'text-danger'}>
                            {testStatus.success ? '✅ Test Passed' : '❌ Test Failed'}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default WebsocketReconnect
